import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";

import { useCurrentPageType, useResponsive } from "shared/hooks";
import { useDeviceDetect } from "shared/hooks/device-detect";
import { PageType } from "shared/lib/page-type";
import { theme } from "shared/lib/styles";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

export const useAppPromotionSnackbar = () => {
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const currentPageType = useCurrentPageType();

  const shouldShow = useMemo(() => {
    const isArticlePage = currentPageType === PageType.Article;
    // cspell: ignore fban fbav
    const isFacebookUserAgent = /fban|fbav/i.test(window.navigator.userAgent);
    return isArticlePage && isFacebookUserAgent && isMobile;
  }, [currentPageType, isMobile]);

  useEffect(() => {
    if (!shouldShow) return;

    const id = enqueueSnackbar({
      anchorOrigin: {
        horizontal: "center",
        vertical: "bottom",
      },
      onClickClose: () => {
        closeSnackbar(id);
      },
      persist: true,
      variant: "appPromotion",
    });

    return () => {
      closeSnackbar(id);
    };
  }, [closeSnackbar, enqueueSnackbar, shouldShow]);
};

export const useAppPromotionTracking = () => {
  const { isAndroid, isIOS } = useDeviceDetect();
  const deviceOS = useMemo(() => {
    if (isIOS) return "ios";
    if (isAndroid) return "android";
    return "";
  }, [isAndroid, isIOS]);

  const trackImpression = useCallback(() => {
    sendGA4Tracking({
      action: "imp",
      category: "appdl",
      customized_parameters: {
        inapp_browser: "fb",
        page_type: "article",
        position: "floating",
      },
      subcategory: "",
    });
  }, []);

  const trackDownloadButtonClick = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "appdl",
      customized_parameters: {
        click_type: "download",
        inapp_browser: "fb",
        os: deviceOS,
        page_type: "article",
        position: "floating",
      },
      subcategory: "",
    });
  }, [deviceOS]);

  const trackCloseButtonClick = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "appdl",
      customized_parameters: {
        click_type: "close",
        inapp_browser: "fb",
        os: deviceOS,
        page_type: "article",
        position: "floating",
      },
      subcategory: "",
    });
  }, [deviceOS]);

  return {
    trackCloseButtonClick,
    trackDownloadButtonClick,
    trackImpression,
  };
};
